import type { AddTeammateRequest, Jurisdiction, PeopleRoster, TeamMember } from "@don/wire";

import type { ReactivateTarget } from "./reactivate.js";

/**
 * Add teammate's rules, as arithmetic (epic 26-10-02-knowledge-v0, issue 215): which messages
 * the form shows, when Add is enabled, what is sent, and the table after an add. Pure, for
 * the reason `team.ts` is; `add.test.ts` asserts each.
 */

/** The 50 states by wire spelling. A `Record` over the wire's own type, so a state the enum
 * gains or loses is a type error here rather than a missing option. */
const STATES: Readonly<Record<Exclude<Jurisdiction, "PH">, string>> = {
  "US-AL": "Alabama", "US-AK": "Alaska", "US-AZ": "Arizona", "US-AR": "Arkansas", "US-CA": "California",
  "US-CO": "Colorado", "US-CT": "Connecticut", "US-DE": "Delaware", "US-FL": "Florida", "US-GA": "Georgia",
  "US-HI": "Hawaii", "US-ID": "Idaho", "US-IL": "Illinois", "US-IN": "Indiana", "US-IA": "Iowa",
  "US-KS": "Kansas", "US-KY": "Kentucky", "US-LA": "Louisiana", "US-ME": "Maine", "US-MD": "Maryland",
  "US-MA": "Massachusetts", "US-MI": "Michigan", "US-MN": "Minnesota", "US-MS": "Mississippi", "US-MO": "Missouri",
  "US-MT": "Montana", "US-NE": "Nebraska", "US-NV": "Nevada", "US-NH": "New Hampshire", "US-NJ": "New Jersey",
  "US-NM": "New Mexico", "US-NY": "New York", "US-NC": "North Carolina", "US-ND": "North Dakota", "US-OH": "Ohio",
  "US-OK": "Oklahoma", "US-OR": "Oregon", "US-PA": "Pennsylvania", "US-RI": "Rhode Island", "US-SC": "South Carolina",
  "US-SD": "South Dakota", "US-TN": "Tennessee", "US-TX": "Texas", "US-UT": "Utah", "US-VT": "Vermont",
  "US-VA": "Virginia", "US-WA": "Washington", "US-WV": "West Virginia", "US-WI": "Wisconsin", "US-WY": "Wyoming",
};

/**
 * Works in's options (design-spec §6 "Form"): California, then Philippines, then the other 49
 * US states A to Z (owner changed 2026-10-05: California and Philippines first).
 */
export const WORKS_IN: readonly { label: string; value: Jurisdiction }[] = [
  { label: STATES["US-CA"], value: "US-CA" },
  { label: "Philippines", value: "PH" },
  ...(Object.entries(STATES) as [Jurisdiction, string][])
    .filter(([value]) => value !== "US-CA")
    .map(([value, label]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

/** The form as typed, which fields have been left, and the address the server refused. */
export interface AddForm {
  readonly name: string;
  readonly email: string;
  /** `""` until a place is chosen. */
  readonly worksIn: Jurisdiction | "";
  readonly touched: { readonly name: boolean; readonly email: boolean };
  /** The address answered `EmailOnTeam`, as `normalEmail` spells it, or `null`. */
  readonly onTeam: string | null;
  /**
   * The address answered `EmailDeactivated` (issue 218), as `normalEmail` spells it, and the
   * deactivated person who holds it; absent or `null` when none was.
   */
  readonly deactivated?: { readonly email: string; readonly target: ReactivateTarget } | null;
}

/** The address as the server stores it: trimmed and lowercased (`normalize_email`). */
export function normalEmail(input: string): string {
  return input.trim().toLowerCase();
}

/**
 * Whether the server will take this as an address: the checks of
 * `registry::identity::normalize_email` (trimmed, ASCII, one `@` with something before it, a
 * domain with a dot not at either end, no whitespace or control character). A Console check
 * so Add is disabled on what the server would refuse with a 400; the server still decides.
 */
export function emailLooksValid(input: string): boolean {
  const email = input.trim();
  if (!/^[\x00-\x7F]*$/.test(email)) return false;
  const at = email.indexOf("@");
  if (at <= 0) return false;
  const domain = email.slice(at + 1);
  return (
    !domain.includes("@") &&
    domain.includes(".") &&
    !domain.startsWith(".") &&
    !domain.endsWith(".") &&
    !/[\s\x00-\x1F\x7F]/.test(email)
  );
}

/**
 * The field messages (design-spec §6 "Field errors"). Name: "Enter a name." once the field
 * has been left blank. Email: "Already on your team." while it is the address the server
 * refused (in any case), else "Enter an email address." once left empty or malformed (§11 D5).
 */
export function addFormMessages(form: AddForm): { name: string | null; email: string | null } {
  const name = form.touched.name && form.name.trim() === "" ? "Enter a name." : null;
  let email: string | null = null;
  if (form.onTeam !== null && normalEmail(form.email) === form.onTeam) email = "Already on your team.";
  else if (form.touched.email && !emailLooksValid(form.email)) email = "Enter an email address.";
  return { name, email };
}

/**
 * The deactivated person Add's offer band names (design-spec §5 `add-form-deactivated-match`):
 * while the Email is the address the server answered `EmailDeactivated` for, in any case, and
 * `null` once it changes.
 */
export function deactivatedMatch(form: AddForm): ReactivateTarget | null {
  const match = form.deactivated ?? null;
  return match !== null && normalEmail(form.email) === match.email ? match.target : null;
}

/**
 * Add is enabled only with all three valid and an address the server has not refused, as on
 * the team or as a deactivated person's (whose offer band stands in its place, issue 218).
 */
export function addFormValid(form: AddForm): boolean {
  return (
    form.name.trim() !== "" &&
    emailLooksValid(form.email) &&
    form.worksIn !== "" &&
    (form.onTeam === null || normalEmail(form.email) !== form.onTeam) &&
    deactivatedMatch(form) === null
  );
}

/** What `POST /people` is sent. Only called once `addFormValid`. */
export function addRequest(form: AddForm): AddTeammateRequest {
  return { display_name: form.name.trim(), email: form.email.trim(), jurisdiction: form.worksIn as Jurisdiction };
}

/** The in-dialog Notice when an add fails (design-spec §6 "Add failed"). */
export function addFailedCopy(name: string): string {
  return `Couldn't add ${name.trim()}. Nothing was changed. Try again.`;
}

/**
 * The roster once the server has added someone: their row, as the server answered it, joins
 * the table, where `orderRoster` places it by name (decision 22). Folding the same answer
 * twice adds it once.
 */
export function withAdded(roster: PeopleRoster, person: TeamMember): PeopleRoster {
  return {
    people: [...roster.people.filter((m) => m.person_id !== person.person_id), person],
  };
}
